import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  PhotoCamera as PhotoCameraIcon,
  TextFields as TextFieldsIcon,
  Lock as LockIcon,
  Check as CheckIcon,
  Dashboard as CockpitIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useThemeMode } from '../../theme/ThemeProvider';
import UserPhotoUpload from '../../components/UserPhotoUpload';
import api from '../../services/api';
import Preloader from '../../components/Preloader';

const PreferenciasUsuario = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { changeTheme, currentTheme } = useThemeMode();
  const {
    preferences,
    isLoading,
    error: prefsError,
    updatePreferences,
    updateTheme,
    updateFontSize,
    saveUserPhoto,
    removeUserPhoto,
    loadPreferences
  } = useUserPreferences();

  const [activeTab, setActiveTab] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Estados para troca de senha
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [showConfirmarSenha, setShowConfirmarSenha] = useState(false);
  const [loadingSenha, setLoadingSenha] = useState(false);

  // Temas disponíveis
  const themes = [
    { key: 'default', name: 'Padrão', color: '#20ac9c', description: 'Tema padrão da aplicação' },
    { key: 'dark', name: 'Escuro', color: '#424242', description: 'Ideal para ambientes com pouca luz' },
    { key: 'citrico', name: 'Cítrico', color: '#FF9800', description: 'Energia com tons de laranja' }
  ];

  // Tamanhos de fonte disponíveis
  const fontSizes = [
    { key: 'small', name: 'Pequeno', icon: 'Aa', size: '0.75rem' },
    { key: 'medium', name: 'Médio', icon: 'Aa', size: '0.875rem' },
    { key: 'large', name: 'Grande', icon: 'Aa', size: '1rem' }
  ];

  // Lidar com mudança de aba
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Selecionar tema
  const handleThemeSelect = async (themeKey) => {
    try {
      // Aplicar tema imediatamente para feedback visual
      changeTheme(themeKey);

      // Salvar nas preferências
      const success = await updateTheme(themeKey);
      if (success) {
        setShowSuccess(true);
        setSuccessMessage(`Tema ${themes.find(t => t.key === themeKey)?.name} aplicado com sucesso!`);
      } else {
        // Reverter se falhou ao salvar
        changeTheme(preferences?.theme_mode || 'default');
        throw new Error('Falha ao salvar tema');
      }
    } catch (error) {
      // Reverter tema em caso de erro
      changeTheme(preferences?.theme_mode || 'default');
      setShowError(true);
      setErrorMessage('Erro ao salvar tema. Tente novamente.');
    }
  };

  // Selecionar tamanho da fonte
  const handleFontSizeSelect = async (fontSizeKey) => {
    try {
      const success = await updateFontSize(fontSizeKey);
      if (success) {
        setShowSuccess(true);
        setSuccessMessage(`Tamanho da fonte alterado para ${fontSizes.find(f => f.key === fontSizeKey)?.name}!`);
        // Aplicar tamanho da fonte globalmente
        document.documentElement.style.fontSize = fontSizes.find(f => f.key === fontSizeKey)?.size || '0.875rem';
      } else {
        throw new Error('Falha ao salvar tamanho da fonte');
      }
    } catch (error) {
      setShowError(true);
      setErrorMessage('Erro ao salvar tamanho da fonte. Tente novamente.');
    }
  };

  // Lidar com mudança de foto
  const handlePhotoChange = async (photoData) => {
    try {
      if (photoData) {
        const success = await saveUserPhoto(photoData.photoUrl, {
          fileName: photoData.fileName,
          mimeType: photoData.mimeType,
          uploadDate: photoData.uploadDate
        });
        if (success) {
          setShowSuccess(true);
          setSuccessMessage('Foto do perfil atualizada com sucesso!');
        }
      } else {
        const success = await removeUserPhoto();
        if (success) {
          setShowSuccess(true);
          setSuccessMessage('Foto removida com sucesso!');
        }
      }
    } catch (error) {
      setShowError(true);
      setErrorMessage('Erro ao atualizar foto. Tente novamente.');
    }
  };

  // Trocar senha
  const handleChangePassword = async () => {
    // Validações
    if (!senhaAtual || !novaSenha || !confirmarSenha) {
      setShowError(true);
      setErrorMessage('Todos os campos são obrigatórios.');
      return;
    }

    if (novaSenha.length < 6) {
      setShowError(true);
      setErrorMessage('A nova senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setShowError(true);
      setErrorMessage('As senhas não coincidem.');
      return;
    }

    if (senhaAtual === novaSenha) {
      setShowError(true);
      setErrorMessage('A nova senha deve ser diferente da senha atual.');
      return;
    }

    try {
      setLoadingSenha(true);
      setShowError(false);

      await api.patch(`/usuario/${user?.id}/senha`, {
        st_senha: novaSenha
      });

      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');

      setShowSuccess(true);
      setSuccessMessage('Senha alterada com sucesso!');
    } catch (error) {
      setShowError(true);
      setErrorMessage(
        error.response?.data?.mensagem ||
        error.response?.data?.erro ||
        'Erro ao alterar senha. Tente novamente.'
      );
    } finally {
      setLoadingSenha(false);
    }
  };

  // Aplicar tamanho da fonte ao carregar
  useEffect(() => {
    if (preferences?.font_size) {
      const fontSizeConfig = fontSizes.find(f => f.key === preferences.font_size);
      if (fontSizeConfig) {
        document.documentElement.style.fontSize = fontSizeConfig.size;
      }
    }
  }, [preferences?.font_size]);

  // Recarregar preferências quando a aba de foto for aberta
  useEffect(() => {
    if (activeTab === 2 && user?.id) {
      loadPreferences();
    }
  }, [activeTab, user?.id, loadPreferences]);

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ minHeight: '400px' }}>
          <Preloader message="Carregando preferências..." />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate('/cockpit')}
              sx={{
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 500 }}>
              Preferências do Usuário
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/cockpit')}
            sx={{ textTransform: 'none' }}
          >
            Voltar
          </Button>
        </Box>

        {/* Erro geral */}
        {prefsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {prefsError}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab icon={<PaletteIcon />} iconPosition="start" label="Temas" />
            <Tab icon={<TextFieldsIcon />} iconPosition="start" label="Fonte" />
            <Tab icon={<PhotoCameraIcon />} iconPosition="start" label="Foto do Perfil" />
            <Tab icon={<CockpitIcon />} iconPosition="start" label="Interface" />
            <Tab icon={<LockIcon />} iconPosition="start" label="Trocar Senha" />
          </Tabs>
        </Box>

        {/* Conteúdo das Tabs */}
        <Box sx={{ minHeight: '400px' }}>
          {/* Aba 1: Temas */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Escolha seu tema preferido
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: 'stretch' }}>
                {themes.map((theme) => (
                  <Box key={theme.key} sx={{ flex: 1 }}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: (preferences?.theme_mode === theme.key || currentTheme === theme.key)
                          ? `2px solid ${theme.color}`
                          : '1px solid #e0e0e0',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleThemeSelect(theme.key)}
                        sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'stretch' }}
                      >
                        <CardContent sx={{ p: 2, flexGrow: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: theme.color
                              }}
                            />
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {theme.name}
                            </Typography>
                            {(preferences?.theme_mode === theme.key || currentTheme === theme.key) && (
                              <CheckIcon sx={{ color: theme.color, ml: 'auto' }} />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {theme.description}
                          </Typography>
                          {(preferences?.theme_mode === theme.key || currentTheme === theme.key) && (
                            <Chip
                              label="Ativo"
                              size="small"
                              sx={{
                                backgroundColor: theme.color,
                                color: 'white',
                                fontSize: '0.75rem',
                                mt: 1
                              }}
                            />
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Aba 2: Tamanho da Fonte */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Ajuste o tamanho da fonte
              </Typography>
              <Grid container spacing={2}>
                {fontSizes.map((fontSize) => (
                  <Grid item xs={12} sm={4} key={fontSize.key}>
                    <Card
                      sx={{
                        border: preferences?.font_size === fontSize.key
                          ? '2px solid #20ac9c'
                          : '1px solid #e0e0e0',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardActionArea onClick={() => handleFontSizeSelect(fontSize.key)}>
                        <CardContent sx={{ p: 2, textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 500,
                              fontSize: fontSize.size,
                              mb: 1
                            }}
                          >
                            {fontSize.icon}
                          </Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
                            {fontSize.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: fontSize.size }}
                          >
                            Texto de exemplo
                          </Typography>
                          {preferences?.font_size === fontSize.key && (
                            <Chip
                              label="Ativo"
                              size="small"
                              sx={{
                                backgroundColor: '#20ac9c',
                                color: 'white',
                                fontSize: '0.75rem',
                                mt: 1
                              }}
                            />
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Dica:</strong> O tamanho da fonte será aplicado a toda a interface do sistema.
                  A configuração será salva automaticamente.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Aba 3: Foto do Perfil */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Gerencie sua foto de perfil
              </Typography>
              <UserPhotoUpload
                userId={user?.id}
                currentPhoto={preferences?.user_photo}
                onPhotoChange={handlePhotoChange}
                size={120}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Formatos aceitos: JPG, PNG ou WebP (máximo 5MB)
              </Typography>
            </Box>
          )}

          {/* Aba 4: Interface */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Personalize a interface do sistema
              </Typography>

              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                  Posição do Menu Lateral
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Escolha se prefere o menu principal do lado direito ou esquerdo da tela.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: preferences?.menu_position === 'left' ? '2px solid #20ac9c' : '1px solid #e0e0e0',
                        bgcolor: preferences?.menu_position === 'left' ? 'action.hover' : 'background.paper'
                      }}
                      onClick={async () => {
                        const success = await updatePreferences({ menu_position: 'left' });
                        if (success) {
                          setShowSuccess(true);
                          setSuccessMessage('Menu movido para a esquerda!');
                        } else {
                          setShowError(true);
                          setErrorMessage('Erro ao salvar preferência.');
                        }
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 40, height: 40, bgcolor: 'grey.300', borderRadius: 1, display: 'flex', justifyContent: 'flex-start', p: 0.5 }}>
                          <Box sx={{ width: 10, height: '100%', bgcolor: 'primary.main', borderRadius: 0.5 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1">Esquerda</Typography>
                          <Typography variant="caption" color="text.secondary">Menu fixo à esquerda</Typography>
                        </Box>
                        {preferences?.menu_position === 'left' && <CheckIcon sx={{ ml: 'auto', color: 'primary.main' }} />}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: (!preferences?.menu_position || preferences?.menu_position === 'right') ? '2px solid #20ac9c' : '1px solid #e0e0e0',
                        bgcolor: (!preferences?.menu_position || preferences?.menu_position === 'right') ? 'action.hover' : 'background.paper'
                      }}
                      onClick={async () => {
                        const success = await updatePreferences({ menu_position: 'right' });
                        if (success) {
                          setShowSuccess(true);
                          setSuccessMessage('Menu movido para a direita!');
                        } else {
                          setShowError(true);
                          setErrorMessage('Erro ao salvar preferência.');
                        }
                      }}
                    >
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 40, height: 40, bgcolor: 'grey.300', borderRadius: 1, display: 'flex', justifyContent: 'flex-end', p: 0.5 }}>
                          <Box sx={{ width: 10, height: '100%', bgcolor: 'primary.main', borderRadius: 0.5 }} />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1">Direita</Typography>
                          <Typography variant="caption" color="text.secondary">Menu fixo à direita (Padrão)</Typography>
                        </Box>
                        {(!preferences?.menu_position || preferences?.menu_position === 'right') && <CheckIcon sx={{ ml: 'auto', color: 'primary.main' }} />}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          )}

          {/* Aba 5: Trocar Senha */}
          {activeTab === 4 && (
            <Box sx={{ maxWidth: '500px', mx: 'auto' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                Alterar senha de acesso
              </Typography>

              <TextField
                fullWidth
                label="Senha Atual"
                type={showSenhaAtual ? 'text' : 'password'}
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                        edge="end"
                      >
                        {showSenhaAtual ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Nova Senha"
                type={showNovaSenha ? 'text' : 'password'}
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                helperText="Mínimo de 6 caracteres"
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNovaSenha(!showNovaSenha)}
                        edge="end"
                      >
                        {showNovaSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <TextField
                fullWidth
                label="Confirmar Nova Senha"
                type={showConfirmarSenha ? 'text' : 'password'}
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                error={confirmarSenha !== '' && novaSenha !== confirmarSenha}
                helperText={confirmarSenha !== '' && novaSenha !== confirmarSenha ? 'As senhas não coincidem' : ''}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmarSenha(!showConfirmarSenha)}
                        edge="end"
                      >
                        {showConfirmarSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleChangePassword}
                disabled={loadingSenha || !senhaAtual || !novaSenha || !confirmarSenha}
                startIcon={loadingSenha ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
                sx={{ py: 1.5 }}
              >
                {loadingSenha ? 'Alterando...' : 'Alterar Senha'}
              </Button>

              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Dica de segurança:</strong> Use uma senha forte com pelo menos 6 caracteres,
                  combinando letras, números e caracteres especiais.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Snackbar de Sucesso */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success" icon={<CheckIcon />}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar de Erro */}
      <Snackbar
        open={showError}
        autoHideDuration={5000}
        onClose={() => setShowError(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PreferenciasUsuario;

